import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  Invoice,
  InvoicesListResponse,
  InvoiceDetailResponse,
  CreateInvoiceInput,
  UpdateInvoiceRequest,
} from '@stfors/shared-types';
import { api, postJsonForBlob } from '../client';

export interface InvoiceListFilters {
  client_id?: number;
  project_id?: number;
}

export function useInvoices(filters: InvoiceListFilters = {}) {
  const params = new URLSearchParams();
  if (filters.client_id !== undefined) params.set('client_id', String(filters.client_id));
  if (filters.project_id !== undefined) params.set('project_id', String(filters.project_id));
  const qs = params.toString();
  const url = qs.length > 0 ? `/api/invoices?${qs}` : '/api/invoices';
  return useQuery({
    queryKey: ['invoices', { client_id: filters.client_id ?? null, project_id: filters.project_id ?? null }],
    queryFn: ({ signal }) => api.get<InvoicesListResponse>(url, signal),
  });
}

export function useInvoiceDetail(id: number | null) {
  return useQuery({
    queryKey: ['invoices', { id }],
    queryFn: ({ signal }) =>
      api.get<InvoiceDetailResponse>(`/api/invoices/${id}`, signal),
    enabled: id !== null,
  });
}

export interface GenerateInvoiceFromTimeInput {
  project_id: number;
  time_entry_ids: number[];
  issue_date: number;
  due_date: number;
  tax_amount?: string;
  notes?: string;
}

export function useGenerateInvoiceFromTime() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: GenerateInvoiceFromTimeInput) =>
      api.post<{ invoice: Invoice }>('/api/invoices/generate-from-time', input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['invoices'] });
      void qc.invalidateQueries({ queryKey: ['project-matters'] });
      void qc.invalidateQueries({ queryKey: ['time-entries'] });
    },
  });
}

/**
 * Render the invoice a `CreateInvoiceInput` WOULD produce, without creating it.
 * Same body as `useCreateInvoice`, and the server builds the lines with the
 * same resolver, so what comes back is the document you are about to commit.
 *
 * Not a mutation — nothing is written, so there is no cache to invalidate.
 */
export async function previewInvoicePdf(input: CreateInvoiceInput): Promise<Blob> {
  const { blob } = await postJsonForBlob('/api/invoices/preview-pdf', input);
  return blob;
}

export function useCreateInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateInvoiceInput) =>
      api.post<{ invoice: Invoice }>('/api/invoices', input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['invoices'] });
      void qc.invalidateQueries({ queryKey: ['project-matters'] });
      void qc.invalidateQueries({ queryKey: ['time-entries'] });
      void qc.invalidateQueries({ queryKey: ['expenses'] });
    },
  });
}

export interface MarkInvoicePaidInput {
  id: number;
  paid_at?: number;
  paid_amount?: string;
  draw_from_retainer_id?: number;
}

export function useMarkInvoicePaid() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: MarkInvoicePaidInput) =>
      api.post<{ invoice: Invoice }>(`/api/invoices/${id}/mark-paid`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['invoices'] });
      void qc.invalidateQueries({ queryKey: ['project-matters'] });
      // Mark-paid may have drawn against a retainer; invalidate so the
      // client detail page balance updates.
      void qc.invalidateQueries({ queryKey: ['retainers'] });
    },
  });
}

export function useSendInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      api.post<{ invoice: Invoice }>(`/api/invoices/${id}/send`, {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['invoices'] });
      void qc.invalidateQueries({ queryKey: ['project-matters'] });
    },
  });
}

export function useVoidInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      api.post<{ invoice: Invoice }>(`/api/invoices/${id}/void`, {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['invoices'] });
      void qc.invalidateQueries({ queryKey: ['project-matters'] });
      // Voiding releases time entries back to unbilled.
      void qc.invalidateQueries({ queryKey: ['time-entries'] });
    },
  });
}

export interface UpdateInvoiceInput extends UpdateInvoiceRequest {
  id: number;
}

export function useUpdateInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...patch }: UpdateInvoiceInput) =>
      api.patch<{ invoice: Invoice }>(`/api/invoices/${id}`, patch),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['invoices'] });
      void qc.invalidateQueries({ queryKey: ['project-matters'] });
    },
  });
}

export function useDeleteInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/api/invoices/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['invoices'] });
      void qc.invalidateQueries({ queryKey: ['project-matters'] });
      // Deleting a draft releases its time entries back to unbilled.
      void qc.invalidateQueries({ queryKey: ['time-entries'] });
    },
  });
}
